import { useEffect, useMemo, useState } from 'react'
import { Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  choicePriceLabel,
  isRequired,
  rulePill,
  type VariantKind,
  type VariantRule,
} from '@/lib/variantShape'

/**
 * The cashier's view, live (F-MOD-N12).
 *
 * A reduced copy of the counter's options sheet, fed straight from editor
 * drafts rather than the catalogue, so an owner sees what a change does
 * while they type. Tapping a choice applies the same pick rules the real
 * sheet uses (single replaces, multi toggles within the limit), and the
 * price moves the way the till will move it.
 *
 * Deliberately NOT the real ModifierConfigurator: that one wants resolved
 * ids, price indexes and a catalogue. This wants a draft.
 */

export interface PreviewQuestion {
  key: string
  prompt: string
  kind: VariantKind
  rule: VariantRule
  choices: Array<{ name: string; price: number; isDefault: boolean; isAvailable: boolean }>
  /** Dim it: an inherited question the owner is not editing right now. */
  muted?: boolean
  /** Outline it: the question being edited. */
  focus?: boolean
}

export function CashierPreview({
  productName,
  basePrice,
  questions,
  formatCurrency,
  footnote,
}: {
  productName: string
  basePrice: number
  questions: PreviewQuestion[]
  formatCurrency: (n: number) => string
  footnote?: string
}) {
  // Selection: question key → set of choice indexes.
  const [sel, setSel] = useState<Record<string, number[]>>({})

  // Re-seed defaults whenever the shape changes (a new choice, a kind switch),
  // but keep picks the owner already made where the index still exists.
  const shape = questions.map((q) => `${q.key}:${q.kind}:${q.choices.length}`).join('|')
  useEffect(() => {
    setSel((prev) => {
      const next: Record<string, number[]> = {}
      for (const q of questions) {
        const kept = (prev[q.key] ?? []).filter((i) => i < q.choices.length)
        if (kept.length > 0) {
          next[q.key] = kept
          continue
        }
        const defaults = q.choices.map((c, i) => (c.isDefault && c.isAvailable ? i : -1)).filter((i) => i >= 0)
        if (defaults.length > 0) next[q.key] = q.kind === 'many' ? defaults : [defaults[0]]
        else if (q.kind === 'size' || (q.kind === 'one' && isRequired(q.rule))) {
          const first = q.choices.findIndex((c) => c.isAvailable)
          next[q.key] = first >= 0 ? [first] : []
        } else next[q.key] = []
      }
      return next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shape])

  const price = useMemo(() => {
    let p = basePrice
    for (const q of questions) {
      const picks = sel[q.key] ?? []
      if (q.kind === 'size') {
        const i = picks[0]
        if (i != null && q.choices[i]) p = q.choices[i].price
      } else {
        for (const i of picks) p += q.choices[i]?.price ?? 0
      }
    }
    return p
  }, [basePrice, questions, sel])

  const pick = (q: PreviewQuestion, i: number) => {
    if (!q.choices[i]?.isAvailable) return
    setSel((prev) => {
      const cur = prev[q.key] ?? []
      const has = cur.includes(i)
      if (q.kind !== 'many') {
        // Single: replace. Tapping the chosen one clears it unless required.
        if (has && !isRequired(q.rule) && q.kind !== 'size') return { ...prev, [q.key]: [] }
        return { ...prev, [q.key]: [i] }
      }
      if (has) return { ...prev, [q.key]: cur.filter((x) => x !== i) }
      if (q.rule.m != null && cur.length >= q.rule.m) return prev
      return { ...prev, [q.key]: [...cur, i] }
    })
  }

  return (
    <div className="space-y-2">
      <div className="rounded-2xl border bg-card p-3 shadow-sm">
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-semibold">{productName || 'Item'}</span>
          <span className="text-sm tabular-nums">{formatCurrency(price)}</span>
        </div>

        {questions.length === 0 ? (
          <p className="rounded-lg border border-dashed px-3 py-5 text-center text-xs text-muted-foreground">
            No questions. One tap adds it to the order.
          </p>
        ) : (
          questions.map((q) => {
            const picks = sel[q.key] ?? []
            const need = isRequired(q.rule) && picks.length < q.rule.n
            const named = q.choices.map((c, i) => ({ ...c, i })).filter((c) => c.name.trim() !== '')
            return (
              <div
                key={q.key}
                className={cn(
                  'mb-3 space-y-1.5 rounded-md',
                  q.muted && 'opacity-50',
                  q.focus && 'outline outline-1 outline-dashed outline-primary/60 outline-offset-4',
                )}
              >
                <div className="flex items-baseline justify-between gap-2 text-xs font-medium">
                  <span className="truncate">{q.prompt || <span className="italic text-muted-foreground">No prompt yet</span>}</span>
                  <span
                    className={cn(
                      'shrink-0 rounded-full px-1.5 py-px text-[10px] font-medium',
                      need
                        ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
                        : isRequired(q.rule)
                          ? 'bg-primary/10 text-primary'
                          : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {need ? 'needed · ' : ''}
                    {rulePill(q.kind, q.rule)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {named.length === 0 ? (
                    <div className="col-span-2 rounded-lg border border-dashed px-2 py-1.5 text-center text-xs text-muted-foreground">
                      No choices yet
                    </div>
                  ) : (
                    named.map((c) => {
                      const on = picks.includes(c.i)
                      return (
                        <button
                          key={c.i}
                          type="button"
                          onClick={() => pick(q, c.i)}
                          className={cn(
                            'flex min-w-0 items-center justify-between gap-1.5 rounded-lg border px-2 py-1.5 text-left text-xs',
                            on && 'border-primary bg-primary/10',
                            !c.isAvailable && 'cursor-not-allowed text-muted-foreground line-through',
                          )}
                        >
                          <span className="truncate">{c.name}</span>
                          <span className="shrink-0 tabular-nums text-muted-foreground">
                            {choicePriceLabel(q.kind, c.price, formatCurrency)}
                          </span>
                        </button>
                      )
                    })
                  )}
                </div>
              </div>
            )
          })
        )}

        <div className="flex items-center justify-between border-t pt-2">
          <span className="flex items-center gap-2 text-xs tabular-nums">
            <span className="grid h-6 w-6 place-items-center rounded-md border"><Minus className="h-3 w-3" /></span>
            1
            <span className="grid h-6 w-6 place-items-center rounded-md border"><Plus className="h-3 w-3" /></span>
          </span>
          <span className="rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground">Add to order</span>
        </div>
      </div>
      {footnote && <p className="text-xs text-muted-foreground">{footnote}</p>}
    </div>
  )
}
